import {radii, headerSizes, tokens} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled, {css} from 'styled-components'

import Image from '@helios/shared/components/image'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import {BackgroundWrapper, Section, Layout} from '@helios/shared/styles/grid'
import {Text, TextLink} from '@helios/shared/styles/typography'
import type ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

type Logo = {
    logo: ImageSource
    title: string
    link: PrismicLinkProps
    linkText: string
}

type Props = {
    logos: Logo[]
    title: string
    subtitle: string
}

const MAX_INTEGRATION_ITEMS: number = 4

export const IntegrationCards = ({title, subtitle, logos}: Props) => (
    <BackgroundWrapper color="muted">
        <Section>
            <Layout variant="two-equal" alignItems={logos.length <= 2 ? 'center' : 'start'}>
                <Content $isSticky={logos.length > 2}>
                    <Text as="h2" variant="heading-base">
                        {title}
                    </Text>
                    <Text variant="paragraph-large">{subtitle}</Text>
                </Content>
                <IntegrationLogos logos={logos.slice(0, MAX_INTEGRATION_ITEMS)} />
            </Layout>
        </Section>
    </BackgroundWrapper>
)

interface IntegrationLogosProps {
    logos: Logo[]
}

const IntegrationLogos = ({logos}: IntegrationLogosProps) => (
    <IntegrationLogosGrid>
        {logos.map(({link, logo, title, linkText}: Logo) => (
            <IntegrationBox>
                <ImageWrapper>
                    <Image image={logo} objectFit="contain" />
                </ImageWrapper>

                <Text variant="title-base">{title}</Text>
                <StyledLink as={PrismicLink} {...link}>
                    {linkText}
                </StyledLink>
            </IntegrationBox>
        ))}
    </IntegrationLogosGrid>
)

const stickyStyles = css`
    ${mq.min('sm')} {
        position: sticky;
        top: calc(${headerSizes.heightSmall}px + ${tokens.spacing24});
        align-self: flex-start;
    }

    ${mq.min('md')} {
        top: calc(${headerSizes.heightLarge}px + ${tokens.spacing48});
    }
`

type ContentProps = {
    $isSticky: boolean
}

const Content = styled.div<ContentProps>`
    display: grid;
    row-gap: ${tokens.spacing20};
    align-self: center;

    ${mq.min('sm')} {
        row-gap: ${tokens.spacing28};
    }

    ${(props) => props.$isSticky && stickyStyles}
`

const IntegrationLogosGrid = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-template-columns: repeat(2, 1fr);
    }
`

const IntegrationBox = styled.div`
    padding: ${tokens.spacing40} ${tokens.spacing20};
    background-color: ${tokens.shade000};
    border-radius: ${radii.arc8};
`

const ImageWrapper = styled.div`
    display: flex;
    max-width: 280px;
    margin-right: auto;
    margin-bottom: ${tokens.spacing20};
    margin-left: auto;
    aspect-ratio: 2.4;
    justify-content: center;

    ${mq.min('sm')} {
        margin-bottom: ${tokens.spacing40};
    }
`

const StyledLink = styled(TextLink)`
    display: inline-block;
    margin-top: ${tokens.spacing16};
`

export const integrationCardsFragment = graphql`
    fragment IntegrationCards on PrismicSlicePageDataBodyIntegrationCards {
        __typename
        primary {
            title
            subtitle
            cta_text
        }
        items {
            name
            cta_link {
                ...PrismicLink
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $fourColumns)
            }
        }
    }
`
